// File: oneportal-shell/src/api/services/thermostatBatchService.ts
// Service that submits the Edit Controls bulk-edit form as a batch of
// per-attribute operations against the RIS Public API.
//
// Endpoint (hit by the SDK):
//   POST /api/v1/devices/thermostats/bulkSettings
//
// SDK method used:
//   IntegrationServicesClient.batch.batchProcessThermostatBatchOperations()
//
// The payload shape is documented on `BatchThermostatOperationModel` in
// `@resideo/web-integration-services-api-client`; each entry in
// `thermostatOperations` is a `{ operationType, payload }` pair whose
// `operationType` must be a `DeviceOperationType` enum string
// (e.g. "ThermostatSetSystemMode").

import { createRisClient } from "../risClient";
import { getApiUrl } from "../apiConfig";
import { apiService } from "../../services/apiService";
import {
  type BatchThermostatOperationModel,
  DeviceOperationType,
  type AsyncJobInitiatedModelApiResponse,
  type ThermostatOperation,
  type ThermostatScheduleRequest,
} from "@resideo/web-integration-services-api-client";

// ─── Domain types ────────────────────────────────────────────────────────────

export type EditControlsSystemMode = "cool" | "heat" | "auto" | "off";
export type EditControlsFanMode = "auto" | "on" | "circulate";

/**
 * Slice of the Edit Controls form values that map to API operations.
 *
 * Every field is optional so callers can send only the values the user
 * actually changed — an omitted field means "skip this op, don't touch
 * this device attribute". Setpoints additionally require `setpointSet:
 * true` to be sent, and `systemMode` must be present alongside them so
 * the service knows whether to emit the heat, cool, or both setpoint ops.
 * (Hold is captured by the form but is not a `DeviceOperationType` and is
 * intentionally omitted here — see `buildThermostatOperations`.)
 */
export interface ThermostatBulkFormValues {
  systemMode?: EditControlsSystemMode;
  fanMode?: EditControlsFanMode;
  /** Whether the user has explicitly set setpoints. `false` = "Mixed" (unset). */
  setpointSet?: boolean;
  heatSetpoint?: number;
  coolSetpoint?: number;
}

// ─── Value mappers ───────────────────────────────────────────────────────────

const SYSTEM_MODE_API: Record<EditControlsSystemMode, string> = {
  cool: "Cool",
  heat: "Heat",
  auto: "Auto",
  off: "Off",
};

const FAN_MODE_API: Record<EditControlsFanMode, string> = {
  auto: "Auto",
  on: "On",
  circulate: "Circulate",
};

// RIS always expects a hold type alongside a manual setpoint change; Edit
// Controls only ever applies a temporary override, so this is constant.
const SETPOINT_STATUS = "TemporaryHold";

// The SDK types `payload` as `Record<string, JsonNode>` (an opaque
// serializer-only type). At the wire level it is just JSON, so we widen
// once here rather than casting at every call site.
const asPayload = (value: Record<string, unknown>) =>
  value as unknown as ThermostatOperation["payload"];

/**
 * Convert the Edit Controls form values into the ordered list of
 * `ThermostatOperation`s to submit.
 *
 * Emission rules (mirror the UI):
 *   • `systemMode`: emitted when provided.
 *   • `coolSetpoint`: emitted when `setpointSet` is true AND `systemMode`
 *     is `cool` or `auto`.
 *   • `heatSetpoint`: emitted when `setpointSet` is true AND `systemMode`
 *     is `heat` or `auto`.
 *   • `fanMode`: emitted when provided.
 *   • `hold`: no corresponding `DeviceOperationType` exists in the SDK — it
 *     is captured by the form for the review summary but not sent.
 *
 * Exported so it can be unit-tested independently of the network call.
 */
export const buildThermostatOperations = (
  values: ThermostatBulkFormValues
): ThermostatOperation[] => {
  const operations: ThermostatOperation[] = [];

  if (values.systemMode) {
    operations.push({
      operationType: DeviceOperationType.THERMOSTAT_SET_SYSTEM_MODE,
      payload: asPayload({
        SystemSwitchValue: SYSTEM_MODE_API[values.systemMode],
      }),
    });
  }

  if (
    values.setpointSet &&
    values.systemMode &&
    (values.systemMode === "cool" || values.systemMode === "auto") &&
    values.coolSetpoint !== undefined
  ) {
    operations.push({
      operationType: DeviceOperationType.THERMOSTAT_SET_COOL_SETPOINT,
      payload: asPayload({
        SetpointValue: values.coolSetpoint,
        SetpointStatus: SETPOINT_STATUS,
      }),
    });
  }
  if (
    values.setpointSet &&
    values.systemMode &&
    (values.systemMode === "heat" || values.systemMode === "auto") &&
    values.heatSetpoint !== undefined
  ) {
    operations.push({
      operationType: DeviceOperationType.THERMOSTAT_SET_HEAT_SETPOINT,
      payload: asPayload({
        SetpointValue: values.heatSetpoint,
        SetpointStatus: SETPOINT_STATUS,
      }),
    });
  }

  if (values.fanMode) {
    operations.push({
      operationType: DeviceOperationType.THERMOSTAT_SET_FAN_MODE,
      payload: asPayload({
        FanSwitch: { Position: FAN_MODE_API[values.fanMode] },
      }),
    });
  }

  return operations;
};

/**
 * Compose the full `BatchThermostatOperationModel` sent as the POST body.
 * Exported for unit tests.
 */
export const buildBulkThermostatRequest = (
  deviceIds: string[],
  values: ThermostatBulkFormValues
): BatchThermostatOperationModel => ({
  deviceIds,
  thermostatOperations: buildThermostatOperations(values),
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Synchronous token reader for the RIS SDK. Same accessor used by
 * `manageDevicesServices` so both services share the token surface.
 */
const getAccessToken = (): string | null =>
  localStorage.getItem("oneportal_auth_token") ??
  localStorage.getItem("authToken");

// ─── Public API ──────────────────────────────────────────────────────────────

export const thermostatBatchService = {
  /**
   * Submit the Edit Controls form as a bulk operation for the given devices.
   *
   * Uses the Resideo `IntegrationServicesClient` (same pattern as
   * `manageDevicesService`). Throws on network / auth failure — callers
   * surface the error via loading / error UI.
   */
  async applyBulkSchedule(
    deviceIds: string[],
    scheduleRequest: ThermostatScheduleRequest
  ): Promise<AsyncJobInitiatedModelApiResponse> {
    await apiService.getAuthToken();
    const platformApiUrl = getApiUrl("risPublicApi");
    const risClient = createRisClient(platformApiUrl, getAccessToken);
    // Strip only ChannelId — ScheduleChangeSource must be kept to match the working payload shape.
    const { ChannelId: _ch, ...schedulePayload } = scheduleRequest as any;
    const requestBody: BatchThermostatOperationModel = {
      deviceIds,
      thermostatOperations: [{
        // SDK enum value is wrong ("ThermostatSetThermostatSchedule"); API expects this string.
        operationType: "ThermostatSetSchedule" as unknown as DeviceOperationType,
        payload: schedulePayload as unknown as ThermostatOperation["payload"],
      }],
    };
    return risClient.batch.batchProcessThermostatBatchOperations(requestBody);
  },

  async applyBulkSettings(
    deviceIds: string[],
    values: ThermostatBulkFormValues
  ): Promise<AsyncJobInitiatedModelApiResponse> {
    // Prime the auth token so the SDK's sync token accessor can read it.
    await apiService.getAuthToken();

    const platformApiUrl = getApiUrl("risPublicApi");
    const risClient = createRisClient(platformApiUrl, getAccessToken);

    const requestBody = buildBulkThermostatRequest(deviceIds, values);
    return risClient.batch.batchProcessThermostatBatchOperations(requestBody);
  },
};

export default thermostatBatchService;
